// components/DashboardProfileHeader.tsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './PublicProfessionalLanding.css';
import './AnimatedCovers.css';

interface Professional {
  nombre: string;
  apellido: string;
  especialidad: string;
  picture?: string;
  cover_photo?: string;
  professional_type?: 'salud' | 'belleza';
  id?: number;
}

interface Props {
  professional: Professional | null;
}

// Helper: clave de localStorage por usuario para guardar la posicion vertical de la portada
const COVER_POS_KEY = 'consultar:coverPositionY';

const DashboardProfileHeader: React.FC<Props> = ({ professional }) => {
  const navigate = useNavigate();
  const [localUserData, setLocalUserData] = useState<Professional | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAvatarLightbox, setShowAvatarLightbox] = useState(false);
  const [isAdjustingCover, setIsAdjustingCover] = useState(false);
  const [coverPositionY, setCoverPositionY] = useState<number>(() => {
    try {
      const v = localStorage.getItem(COVER_POS_KEY);
      const n = v ? parseInt(v, 10) : 50;
      return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 50;
    } catch { return 50; }
  });

  // Guardar posicion en localStorage cuando cambia
  useEffect(() => {
    try { localStorage.setItem(COVER_POS_KEY, String(coverPositionY)); } catch {}
  }, [coverPositionY]);

  // Efecto para escuchar cambios en localStorage y eventos personalizados
  useEffect(() => {
    const updateUserData = () => {
      try {
        setIsLoading(true);
        const userData = localStorage.getItem('user');
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setLocalUserData({
            nombre: parsedUser.nombre,
            apellido: parsedUser.apellido,
            especialidad: parsedUser.especialidad,
            picture: parsedUser.picture,
            cover_photo: parsedUser.cover_photo,
            professional_type: parsedUser.professional_type,
            id: parsedUser.id
          });
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Cargar datos iniciales
    updateUserData();

    // Escuchar cambios en localStorage (desde otras pestañas)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        console.log('📦 Storage event: user data updated');
        updateUserData();
      }
    };

    // Escuchar cambios personalizados (desde la misma pestaña)
    const handleCustomEvent = () => {
      console.log('🎯 Custom event: userDataUpdated received');
      updateUserData();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userDataUpdated', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataUpdated', handleCustomEvent);
    };
  }, []);

  // Usar datos de localStorage si están disponibles, sino usar los del prop
  const displayProfessional = localUserData || professional;

  if (isLoading || !displayProfessional) {
    // Esqueleto de carga para una mejor experiencia de usuario
    return (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8 animate-pulse">
            <div className="h-36 sm:h-48 bg-gray-300 portada-curva"></div>
            <div className="relative px-5 sm:px-6 pb-5 sm:pb-6">
                <div className="flex items-start gap-4 sm:gap-6">
                    <div className="-mt-10 sm:-mt-14 flex-shrink-0">
                        <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-gray-300 border-4 border-white shadow-lg"></div>
                    </div>
                    <div className="flex-1 min-w-0 pt-1.5 sm:pt-2">
                        <div className="h-6 bg-gray-300 rounded w-3/4 mb-2"></div>
                        <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                    </div>
                </div>
            </div>
        </div>
    );
  }

  const { nombre, apellido, especialidad, picture, cover_photo, professional_type } = displayProfessional;
  const displayName = `${nombre} ${apellido}`;
  const salud = professional_type === 'salud';

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
      {/* Portada: el mismo sistema que la landing pública (oct-2026): en salud
          los colores del logo con círculos suaves, y en todas el borde de abajo
          en arco (.portada-curva, en PublicProfessionalLanding.css). Más baja
          en el celular: es la pantalla de trabajo del día. */}
      <div
        className={`h-36 sm:h-48 relative group overflow-hidden portada-curva ${
          cover_photo
            ? 'bg-gray-200'
            : `animated-cover animated-cover--calm animated-cover--${salud ? 'marca' : 'belleza'}`
        }`}
      >
        {cover_photo ? (
            <img
              src={cover_photo}
              alt="Foto de portada"
              className="w-full h-full object-cover transition-all duration-200"
              style={{ objectPosition: `center ${coverPositionY}%` }}
              onError={(e) => {
                // Fallback si la imagen no carga
                e.currentTarget.style.display = 'none';
              }}
            />
        ) : salud ? (
          <>
            <span className="portada-mancha portada-mancha--1" aria-hidden="true" />
            <span className="portada-mancha portada-mancha--2" aria-hidden="true" />
            <span className="portada-mancha portada-mancha--3" aria-hidden="true" />
          </>
        ) : null}
        <div className="absolute inset-0 bg-black bg-opacity-10 pointer-events-none"></div>

        {/* Editar perfil: un engranaje en la esquina de la portada. Antes era
            un botón gris a todo el ancho debajo del nombre. */}
        <button
          type="button"
          onClick={() => navigate('/professional/profile-config')}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/25 ring-1 ring-white/40 text-white flex items-center justify-center hover:bg-white/35 active:scale-95 transition"
          aria-label="Editar perfil"
          title="Editar perfil"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>

        {/* Boton para ajustar posicion de portada (arriba a la izquierda: la
            derecha es del engranaje) */}
        {cover_photo && (
          <button
            type="button"
            onClick={() => setIsAdjustingCover(v => !v)}
            className="absolute top-3 left-3 z-10 bg-black/40 hover:bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5 transition-opacity opacity-0 group-hover:opacity-100 focus:opacity-100"
            title="Ajustar posicion de la portada"
          >
            {isAdjustingCover ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Listo</span>
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
                <span>Ajustar portada</span>
              </>
            )}
          </button>
        )}

        {/* Slider vertical para ajustar la posicion */}
        {cover_photo && isAdjustingCover && (
          <div className="absolute left-3 top-14 bottom-3 z-10 flex flex-col items-center bg-black/50 backdrop-blur-sm rounded-full px-2 py-3">
            <span className="text-white text-[10px] font-bold mb-1">Arriba</span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={coverPositionY}
              onChange={(e) => setCoverPositionY(parseInt(e.target.value, 10))}
              className="cover-pos-slider"
              style={{
                writingMode: 'vertical-lr' as any,
                WebkitAppearance: 'slider-vertical' as any,
                width: 6,
                height: '100%',
                accentColor: '#3b82f6'
              }}
              aria-label="Posicion vertical de la portada"
            />
            <span className="text-white text-[10px] font-bold mt-1">Abajo</span>
          </div>
        )}
      </div>

      {/* Foto a la izquierda, sobre el arco, y al lado el nombre y la
          especialidad, alineados a la izquierda (text-left: la app centra todo
          por defecto). En una fila: ocupa la mitad de alto que antes.
          items-start: con un nombre largo (3 o 4 renglones en un celular
          angosto) el nombre crece hacia abajo y la foto sigue sobre el arco;
          con items-end la foto bajaba con el nombre. */}
      <div className="relative px-5 sm:px-6 pb-5 sm:pb-6 text-left">
        <div className="flex items-start gap-4 sm:gap-6">
          <div className="-mt-10 sm:-mt-14 flex-shrink-0">
            <div
              className={`w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden border-4 border-white shadow-lg bg-white ${picture ? 'cursor-zoom-in transition-transform hover:scale-105' : ''}`}
              onClick={() => picture && setShowAvatarLightbox(true)}
              title={picture ? 'Click para ampliar' : ''}
            >
              {picture ? (
                <img
                  src={picture}
                  alt={displayName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback si la imagen no carga
                    const imgElement = e.currentTarget as HTMLImageElement;
                    imgElement.style.display = 'none';
                    const fallbackDiv = imgElement.parentNode?.querySelector('.fallback-avatar') as HTMLDivElement;
                    if (fallbackDiv) fallbackDiv.style.display = 'flex';
                  }}
                />
              ) : null}
              
              {/* Fallback avatar siempre presente pero oculto por defecto */}
              <div 
                className={`fallback-avatar w-full h-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center ${
                  picture ? 'hidden' : 'flex'
                }`}
              >
                <span className="text-3xl font-bold text-white">
                  {nombre.charAt(0)}{apellido.charAt(0)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0 pt-1.5 sm:pt-2">
            <h1 className="texto-equilibrado text-[22px] sm:text-3xl font-bold text-gray-900 leading-tight break-words">
              {displayName}
            </h1>
            {especialidad && (
              <p className={`especialidad-marca${salud ? '' : ' especialidad-marca--belleza'} inline-block text-[15px] sm:text-lg font-semibold mt-0.5`}>
                {especialidad}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox del avatar (mismo estilo que PublicProfessionalLanding).
          Va por portal a document.body: el wrapper animate-slide-in-left del
          dashboard (y el overflow-hidden del propio header) atrapaban/recortaban
          este position:fixed. */}
      {showAvatarLightbox && picture && createPortal(
        <div className="avatar-lightbox" onClick={() => setShowAvatarLightbox(false)}>
          <div className="avatar-lightbox-backdrop" />
          <div className="avatar-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={picture}
              alt={displayName}
              className="avatar-lightbox-img"
            />
            <div className="text-center mt-4">
              <h3 className="text-white text-xl font-bold">{displayName}</h3>
              {especialidad && (
                <p className="text-white/60 text-sm mt-1">{especialidad}</p>
              )}
            </div>
            <button
              onClick={() => setShowAvatarLightbox(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors bg-black/30 rounded-full p-2"
              aria-label="Cerrar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DashboardProfileHeader;